import { useRef, useState } from "react";
import { uploadToR2 } from "../lib/studioApi";
import { toast } from "sonner";

const MAX_BY_KIND = {
  audio: 100 * 1024 * 1024,
  video: 500 * 1024 * 1024,
  image: 20 * 1024 * 1024,
  file: 100 * 1024 * 1024,
};

function detectKind(file) {
  const t = (file.type || "").toLowerCase();
  if (t.startsWith("audio/")) return "audio";
  if (t.startsWith("video/")) return "video";
  if (t.startsWith("image/")) return "image";
  return "file";
}

/**
 * useDmAttachments — shared pending-uploads state machine for DM composers.
 * - addFiles(FileList) starts uploads to R2
 * - removePending(id) drops one
 * - clear() drops all
 * - finalize() returns the array of completed attachment objects (or null if any still uploading)
 */
export default function useDmAttachments({ asAdmin = false } = {}) {
  const [pending, setPending] = useState([]); // {id, file, kind, progress, attachment}
  const counterRef = useRef(0);

  const uploadOne = (item) => {
    uploadToR2(item.file, item.kind, {
      asAdmin,
      onProgress: (pct) => {
        setPending((p) => p.map((x) => (x.id === item.id ? { ...x, progress: pct } : x)));
      },
    })
      .then((attachment) => {
        setPending((p) =>
          p.map((x) =>
            x.id === item.id
              ? { ...x, progress: 100, attachment: { ...attachment, kind: item.kind } }
              : x,
          ),
        );
      })
      .catch(() => {
        toast.error(`Upload failed: ${item.file.name}`);
        setPending((p) => p.filter((x) => x.id !== item.id));
      });
  };

  const addFiles = (files) => {
    const next = [];
    for (const file of Array.from(files)) {
      const kind = detectKind(file);
      const max = MAX_BY_KIND[kind];
      if (file.size > max) {
        toast.error(
          `${file.name} is too large. Max ${Math.round(max / (1024 * 1024))}MB for ${kind}`,
        );
        continue;
      }
      counterRef.current += 1;
      next.push({
        id: `att-${counterRef.current}`,
        file,
        kind,
        progress: 0,
        attachment: null,
      });
    }
    setPending((p) => [...p, ...next]);
    next.forEach(uploadOne);
  };

  const removePending = (id) => setPending((p) => p.filter((x) => x.id !== id));
  const clear = () => setPending([]);

  const allDone =
    pending.length > 0 && pending.every((x) => !!x.attachment);

  return {
    pending,
    addFiles,
    removePending,
    clear,
    allDone,
    finalize: () => pending.map((p) => p.attachment).filter(Boolean),
  };
}
